/**
 * 配置型下拉数据本地缓存(localStorage + TTL)
 *
 * 用途: 机构 / 供应商 / 收款方式字典 等"写少读多" + 全量小数据
 *  - 登录时统一清空 + 预热(刷新数据)
 *  - 平时使用走缓存,过期自动回源
 *  - 不接入实时性强或大体积数据(如订单列表、用户列表)
 */

const PREFIX = 'opts:';
const DEFAULT_TTL_HOURS = 4;  // 3-5 小时区间取中值

/**
 * 从缓存取数据,过期或不存在返回 null
 * @param {string} key 缓存键(不含前缀)
 * @returns {any|null}
 */
export function getCached(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const { data, expireAt } = JSON.parse(raw);
    if (!expireAt || Date.now() > expireAt) {
      localStorage.removeItem(PREFIX + key);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * 写入缓存
 * @param {string} key 缓存键
 * @param {any} data 数据(必须可 JSON 序列化)
 * @param {number} ttlHours 有效期小时数, 默认 4 小时
 */
export function setCached(key, data, ttlHours = DEFAULT_TTL_HOURS) {
  try {
    const payload = {
      data,
      expireAt: Date.now() + ttlHours * 3600 * 1000,
      cachedAt: Date.now()
    };
    localStorage.setItem(PREFIX + key, JSON.stringify(payload));
  } catch (e) {
    // QuotaExceeded 等错误吞掉,缓存失败不影响主流程
    console.warn('[optionsCache] write failed:', e.message);
  }
}

/**
 * 清空所有 options 缓存
 * 推荐时机: 登录成功后(确保用户切换账号时不串数据), 显式刷新按钮
 */
export function clearAllOptionsCache() {
  try {
    Object.keys(localStorage).forEach(k => {
      if (k.startsWith(PREFIX)) localStorage.removeItem(k);
    });
  } catch (e) { /* ignore */ }
}

/**
 * 通用包装: 优先命中缓存, miss 时调 fetcher 取数据并写缓存
 * @param {string} key 缓存键
 * @param {Function} fetcher async 函数, 返回需要缓存的数据(返回 null/undefined 不缓存)
 * @param {number} ttlHours 有效期
 * @returns {Promise<any>}
 */
export async function getOrFetch(key, fetcher, ttlHours = DEFAULT_TTL_HOURS) {
  const hit = getCached(key);
  if (hit !== null) return hit;
  const fresh = await fetcher();
  if (fresh != null) setCached(key, fresh, ttlHours);
  return fresh;
}

// ===== 业务封装: 结算单管理用的下拉数据 =====
import { getOrgListNew } from '@/request/api.js';
import { aiDictApi } from '@/views/type_assistant/z_aiaudit/api.js';

const CACHE_KEYS = {
  ORG_LIST: 'orgList',
  SUPPLIER_LIST: 'supplierList',
  COLLECT_WAY: 'collectWay'
};

/** 机构列表(全部) */
export function getOrgListCached() {
  return getOrFetch(CACHE_KEYS.ORG_LIST, async () => {
    const r = await getOrgListNew({});
    return r.code === 200 ? (r.data || []) : null;
  });
}

/** 供应商列表 */
export function getSupplierListCached() {
  return getOrFetch(CACHE_KEYS.SUPPLIER_LIST, async () => {
    const r = await getOrgListNew({ orgType: 'SUPPLIER' });
    return r.code === 200 ? (r.data || []) : null;
  });
}

/** 收款方式字典 */
export function getCollectWayCached() {
  return getOrFetch(CACHE_KEYS.COLLECT_WAY, async () => {
    const r = await aiDictApi.options('collect_way');
    return r.code === 200 ? (r.data || []) : null;
  });
}

/**
 * 登录成功后调用: 清旧缓存 + 后台预热所有 options
 * 不阻塞登录, 失败不报错
 */
export function refreshOptionsAfterLogin() {
  clearAllOptionsCache();
  // 并行预热,失败不影响主流程
  Promise.all([
    getOrgListCached().catch(() => null),
    getSupplierListCached().catch(() => null),
    getCollectWayCached().catch(() => null)
  ]).catch(() => null);
}
